<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  businessHoursEnabled: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['validityChange']);
const enabled = defineModel('enabled', { type: Boolean, default: false });
const gracePeriodMinutes = defineModel('gracePeriodMinutes', {
  type: Number,
  default: 0,
});
const { t } = useI18n();

const isValid = computed(
  () =>
    !enabled.value ||
    (Number.isInteger(gracePeriodMinutes.value) &&
      gracePeriodMinutes.value >= 0)
);

watch(
  () => props.businessHoursEnabled,
  value => {
    if (!value) enabled.value = false;
  },
  { immediate: true }
);

watch(isValid, value => emit('validityChange', value), { immediate: true });
</script>

<template>
  <section
    class="mt-8 flex flex-col gap-3 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak px-4 py-3"
  >
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.BUSINESS_HOURS.AGENT_ACCESS.TITLE') }}
        </label>
        <p class="text-body-main text-n-slate-11">
          {{ t('INBOX_MGMT.BUSINESS_HOURS.AGENT_ACCESS.DESCRIPTION') }}
        </p>
      </div>
      <ToggleSwitch v-model="enabled" :disabled="!businessHoursEnabled" />
    </div>

    <p v-if="!businessHoursEnabled" class="text-label-small text-n-slate-11">
      {{ t('INBOX_MGMT.BUSINESS_HOURS.AGENT_ACCESS.REQUIRES_BUSINESS_HOURS') }}
    </p>

    <div v-if="enabled" class="flex flex-col gap-1">
      <label
        for="agent-access-grace-period"
        class="text-label-medium text-n-slate-12"
      >
        {{ t('INBOX_MGMT.BUSINESS_HOURS.AGENT_ACCESS.GRACE_LABEL') }}
      </label>
      <input
        id="agent-access-grace-period"
        v-model.number="gracePeriodMinutes"
        type="number"
        min="0"
        step="1"
        class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-body-main text-n-slate-12 outline-none focus:border-n-brand"
        :aria-invalid="!isValid"
      />
      <p class="text-label-small text-n-slate-11">
        {{ t('INBOX_MGMT.BUSINESS_HOURS.AGENT_ACCESS.GRACE_HELP') }}
      </p>
      <p v-if="!isValid" class="text-label-small text-n-ruby-9">
        {{ t('INBOX_MGMT.BUSINESS_HOURS.AGENT_ACCESS.GRACE_ERROR') }}
      </p>
    </div>
  </section>
</template>
